import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { Printer, Play, PauseIcon } from "lucide-react";
import { brl, timeBR } from "@/lib/format";
import { printOrder } from "@/lib/print";

// Track already-printed order ids per tenant
const KEY = (rid) => `pg_printed_${rid}`;

export default function PrintBridge() {
  const { restaurant } = useAuth();
  const [orders, setOrders] = useState([]);
  const [auto, setAuto] = useState(true);
  const [trigger, setTrigger] = useState(() => localStorage.getItem("pg_print_trigger") || "recebido");
  const changeTrigger = (v) => { setTrigger(v); localStorage.setItem("pg_print_trigger", v); };
  const [triggerOrder, setTriggerOrder] = useState(null);
  const printedRef = useRef(new Set(JSON.parse(localStorage.getItem(KEY(restaurant?.id)) || "[]")));
  const firstLoad = useRef(true);

  const load = async () => setOrders((await api.get("/orders")).data);
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []);

  // auto-print: new orders (recebido) or ready orders, printed once each via hidden iframe (no dialog with kiosk printing)
  useEffect(() => {
    if (!auto || orders.length === 0) return;
    if (firstLoad.current) {
      // never print the whole backlog on first load
      orders.forEach((o) => printedRef.current.add(o.id));
      localStorage.setItem(KEY(restaurant?.id), JSON.stringify([...printedRef.current]));
      firstLoad.current = false; return;
    }
    const match = trigger === "recebido" ? (o) => o.status !== "cancelado" : (o) => o.status === "pronto";
    const pending = orders.filter((o) => match(o) && !printedRef.current.has(o.id));
    if (pending.length === 0) return;
    const o = pending[0];
    printedRef.current.add(o.id);
    localStorage.setItem(KEY(restaurant?.id), JSON.stringify([...printedRef.current]));
    setTriggerOrder(o); printOrder(o, restaurant);
    setTimeout(() => setTriggerOrder(null), 1500);
  }, [orders, auto, trigger, restaurant]);

  const manualPrint = (o) => { setTriggerOrder(o); printOrder(o, restaurant); setTimeout(() => setTriggerOrder(null), 800); };
  const clearPrinted = () => { printedRef.current.clear(); localStorage.removeItem(KEY(restaurant?.id)); };

  return (
    <div className="space-y-6" data-testid="print-page">
      <div className="print:hidden flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-stone-900">Impressão de cupons</h1>
          <p className="text-stone-500">Deixe esta aba aberta no computador do caixa: cada pedido novo é impresso automaticamente.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={trigger} onChange={(e) => changeTrigger(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="print-trigger">
            <option value="recebido">Imprimir ao receber o pedido</option>
            <option value="pronto">Imprimir quando ficar pronto</option>
          </select>
          <span className="text-sm text-stone-600">Automático</span>
          <Switch checked={auto} onCheckedChange={setAuto} data-testid="print-auto-toggle" />
          <Button variant="outline" size="sm" onClick={clearPrinted} data-testid="print-clear">Resetar histórico</Button>
        </div>
      </div>
      <Card className="print:hidden"><CardContent className="p-4 text-xs text-stone-600 space-y-1">
        <div><strong>Impressão sem caixa de diálogo e sem licença:</strong> não é necessário nenhum programa pago. Abra o Chrome/Edge com o atalho <code className="bg-stone-100 px-1 rounded">chrome.exe --kiosk-printing</code> (Windows) e defina sua impressora térmica como padrão do sistema — os cupons saem direto, sem perguntar.</div>
        <div>Se aparecer aviso de "licença para uso comercial", ele vem de um <em>driver/impressora virtual de PDF</em> de terceiros instalado no computador, não do Portal Gestão. Selecione a impressora térmica real como padrão.</div>
        <div>Cada pedido é impresso apenas uma vez — o histórico fica salvo neste computador.</div>
      </CardContent></Card>

      <div className="print:hidden grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {orders.filter((o) => o.status !== "cancelado").slice(0, 12).map((o) => (
          <Card key={o.id} className="rounded-xl" data-testid={`print-card-${o.number}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-black text-2xl">#{o.number}</div>
                  <div className="text-xs text-stone-500">{o.customer_name || o.channel} • {timeBR(o.created_at)}</div>
                </div>
                <Button size="sm" variant="outline" className="gap-2" onClick={() => manualPrint(o)} data-testid={`print-btn-${o.number}`}>
                  <Printer className="h-4 w-4" /> Imprimir
                </Button>
              </div>
              <div className="text-xs text-stone-500 mt-2">{o.items.length} itens • {brl(o.total)}</div>
              {printedRef.current.has(o.id) && <div className="text-[10px] text-emerald-600 mt-1 font-semibold">✓ Já impresso</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Printable cupom — only visible in print */}
      {triggerOrder && (
        <div id="pg-cupom" className="hidden print:block print:absolute print:inset-0 print:p-4 print:text-black">
          <div style={{ fontFamily: "monospace", fontSize: 12, width: "80mm" }}>
            <div style={{ textAlign: "center", fontWeight: 900, fontSize: 16 }}>{restaurant?.name}</div>
            <div style={{ textAlign: "center", fontSize: 10 }}>{restaurant?.address || ""}</div>
            <div style={{ textAlign: "center", fontSize: 10 }}>{restaurant?.phone || ""}</div>
            <hr style={{ margin: "6px 0", border: "1px dashed #000" }} />
            <div style={{ fontSize: 14, fontWeight: 900 }}>PEDIDO #{triggerOrder.number}</div>
            <div>Canal: {triggerOrder.channel}</div>
            <div>Cliente: {triggerOrder.customer_name || "-"}</div>
            {triggerOrder.customer_phone && <div>Tel: {triggerOrder.customer_phone}</div>}
            {triggerOrder.address && <div>End: {triggerOrder.address}</div>}
            <div>Pagamento: {triggerOrder.payment_method}</div>
            <div>Hora: {timeBR(triggerOrder.created_at)}</div>
            <hr style={{ margin: "6px 0", border: "1px dashed #000" }} />
            {triggerOrder.items.map((i, idx) => (
              <div key={idx} style={{ display: "flex", justifyContent: "space-between" }}>
                <span>{i.quantity}x {i.name}</span>
                <span>{brl(i.price * i.quantity)}</span>
              </div>
            ))}
            <hr style={{ margin: "6px 0", border: "1px dashed #000" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>Subtotal</span><span>{brl(triggerOrder.subtotal)}</span></div>
            {triggerOrder.delivery_fee > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>Entrega</span><span>{brl(triggerOrder.delivery_fee)}</span></div>}
            {triggerOrder.discount > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>Desconto</span><span>-{brl(triggerOrder.discount)}</span></div>}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 900 }}>
              <span>TOTAL</span><span>{brl(triggerOrder.total)}</span>
            </div>
            {triggerOrder.notes && <><hr style={{ margin: "6px 0", border: "1px dashed #000" }} /><div>Obs: {triggerOrder.notes}</div></>}
            <div style={{ textAlign: "center", marginTop: 10, fontSize: 10 }}>Obrigado!</div>
          </div>
        </div>
      )}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #pg-cupom, #pg-cupom * { visibility: visible; }
          #pg-cupom { position: absolute; left: 0; top: 0; }
          @page { size: 80mm auto; margin: 4mm; }
        }
      `}</style>
    </div>
  );
}
